import React, { useState, useEffect } from 'react';
import AdminLayout from '../../../components/admin/layout/AdminLayout';
import { adminService } from '../../../services/admin/adminService';

const AdminSystemMonitor = () => {
  const [systemData, setSystemData] = useState({});
  const [healthData, setHealthData] = useState({});
  const [statsData, setStatsData] = useState({});
  const [tokensData, setTokensData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSystemData();
  }, []);

  const fetchSystemData = async () => {
    try {
      setLoading(true);
      setError(''); // Clear previous errors
      
      console.log('🔄 Fetching system data...');
      
      // Fetch all system data in parallel using admin service
      const [healthResponse, statsResponse, tokensResponse] = await Promise.all([
        adminService.getSystemHealth().catch(err => {
          console.error('Health check failed:', err);
          return { data: { status: 'error', message: 'Health check failed' } };
        }),
        adminService.getSystemStats().catch(err => {
          console.error('Stats failed:', err);
          return { data: { status: 'error', message: 'Stats failed' } };
        }),
        adminService.getSystemTokens().catch(err => {
          console.error('Tokens failed:', err);
          return { data: { status: 'error', message: 'Tokens failed' } };
        })
      ]);
      
      console.log('✅ System data responses:', {
        health: healthResponse.data,
        stats: statsResponse.data,
        tokens: tokensResponse.data
      });
      
      setHealthData(healthResponse.data);
      setStatsData(statsResponse.data);
      setTokensData(tokensResponse.data);
      
    } catch (error) {
      console.error('❌ Error fetching system data:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
      setError(`Failed to load system data: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getHealthStatusColor = (status) => {
    return status === 'healthy' ? 'text-green-600' : 'text-red-600';
  };

  const getHealthStatusIcon = (status) => {
    return status === 'healthy' ? (
      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ) : (
      <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
      </svg>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hours ago`;
    return `${Math.floor(diffInMinutes / 1440)} days ago`;
  };

  return (
    <AdminLayout>
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-2xl font-semibold text-gray-900">System Monitor</h1>
            <p className="mt-2 text-sm text-gray-700">
              Monitor system health, statistics, and active sessions.
            </p>
          </div>
          <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
            <button
              onClick={fetchSystemData}
              disabled={loading}
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-orange-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 sm:w-auto disabled:opacity-50"
            >
              {loading ? 'Refreshing...' : 'Refresh Data'}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="mt-8 flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
          </div>
        ) : error ? (
          <div className="mt-8 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error loading system data</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-8 space-y-6">
            {/* System Health */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">System Health</h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">Email Service</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      {getHealthStatusIcon(healthData.services?.email)}
                      <span className={`ml-2 text-sm font-medium ${getHealthStatusColor(healthData.services?.email)}`}>
                        {healthData.services?.email || 'Unknown'}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-5 5v-5zM4 19h6v-2H4v2zM4 15h6v-2H4v2zM4 11h6V9H4v2zM4 7h6V5H4v2zM10 7h10V5H10v2zM10 11h10V9H10v2zM10 15h10v-2H10v2zM10 19h10v-2H10v2z" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">FCM Service</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      {getHealthStatusIcon(healthData.services?.fcm)}
                      <span className={`ml-2 text-sm font-medium ${getHealthStatusColor(healthData.services?.fcm)}`}>
                        {healthData.services?.fcm || 'Unknown'}
                      </span>
                    </div>
                  </div>
                </div>
                {healthData.timestamp && (
                  <p className="mt-4 text-sm text-gray-500">
                    Last checked: {formatDate(healthData.timestamp)}
                  </p>
                )}
              </div>
            </div>

            {/* System Statistics */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">System Statistics</h3>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                  {statsData.stats && Object.entries(statsData.stats).map(([key, value]) => (
                    <div key={key} className="text-center">
                      <div className="text-2xl font-bold text-gray-900">{value}</div>
                      <div className="text-sm text-gray-500 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                    </div>
                  ))}
                </div>
                {statsData.timestamp && (
                  <p className="mt-4 text-sm text-gray-500">
                    Last updated: {formatDate(statsData.timestamp)}
                  </p>
                )}
              </div>
            </div>

            {/* Active Sessions */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Active Sessions</h3>
                <div className="text-center mb-4">
                  <div className="text-3xl font-bold text-gray-900">{tokensData.count || 0}</div>
                  <div className="text-sm text-gray-500">Active JWT Tokens</div>
                </div>
                
                {tokensData.tokens && tokensData.tokens.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Email
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Created
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Expires
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {tokensData.tokens.slice(0, 10).map((token) => (
                          <tr key={token.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {token.email}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatTimeAgo(token.createdAt)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatTimeAgo(token.expiresAt)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                new Date(token.expiresAt) > new Date() 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {new Date(token.expiresAt) > new Date() ? 'Active' : 'Expired'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {tokensData.tokens.length > 10 && (
                      <p className="mt-2 text-sm text-gray-500 text-center">
                        Showing first 10 of {tokensData.tokens.length} tokens
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-center text-gray-500">No active sessions found</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSystemMonitor; 